import { always } from 'purify-ts/Function';
import { Maybe, Nothing } from 'purify-ts/Maybe';
import { cache } from '@codegrade/utils';
import { WarningHeader } from './warnings';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class ApiResult {
    status;
    body;
    headers;
    cgResult;
    callbacks;
    maybeExtraWarnings;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _warnings;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _onAfterSuccess;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _onSuccess;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(status, body, headers, cgResult, callbacks, maybeExtraWarnings = Nothing) {
        this.status = status;
        this.body = body;
        this.headers = headers;
        this.cgResult = cgResult;
        this.callbacks = callbacks;
        this.maybeExtraWarnings = maybeExtraWarnings;
        this._warnings = new cache.Cache(() => {
            const warningHeaders = WarningHeader.fromHeaders(this.headers);
            return this.maybeExtraWarnings.mapOrDefault(extraWarnings => warningHeaders.merge(extraWarnings), warningHeaders);
        });
        this._onAfterSuccess = new cache.Cache(() => this._makeCallback('onAfterSuccess'));
        this._onSuccess = new cache.Cache(() => this._makeCallback('onSuccess'));
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _makeCallback(callback) {
        const callbacks = this.callbacks;
        return async function _makeCallbackResult() {
            for (const meths of callbacks) {
                const meth = meths[callback];
                if (meth != null) {
                    // eslint-disable-next-line no-await-in-loop
                    await meth.bind(this)(this);
                }
            }
        };
    }
    /**
     * Add the warning of another `ApiResult` to this result.
     *
     * @param response The response to add the the warnings off.
     * @returns A fresh `ApiResult` which contains all the data of `this` and the warnings of the
     * given `response`. Note that the callbacks of `response` are not copied over to the returned
     * `ApiResult`, but the callbacks of `this` are.
     */
    addWarnings(response) {
        if (response.warnings.messages.length === 0) {
            return this;
        }
        const newExtraWarnings = this.maybeExtraWarnings.mapOrDefault(extraWarnings => response.warnings.merge(extraWarnings), response.warnings);
        return new ApiResult(this.status, this.body, this.headers, this.cgResult, this.callbacks, Maybe.of(newExtraWarnings));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get warnings() {
        return this._warnings.v;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromResponse(response) {
        return ApiResult.fromResponseAndValue(response, response.body);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromResponseAndValue(response, value) {
        return new ApiResult(response.status, value, response.headers, value, []);
    }
    /**
     * The `onAfterSuccess` callback. This callback should be called directly after the
     * `onSuccess` callback and when the user has been notified that the requests have succeeded.
     */
    get onAfterSuccess() {
        return this._onAfterSuccess.v;
    }
    /**
     * The `onSuccess` callback. This callback should be called directly after it is know
     * that the requests have succeeded.
     */
    get onSuccess() {
        return this._onSuccess.v;
    }
    /**
     * The parsed body of the response.
     *
     * @deprecated Use the `body` attribute instead, or better `cgResult`.
     */
    get data() {
        return this.body;
    }
    /**
     * Map the `cgResult` of this response.
     *
     * @param mapper The mapper function, called with `cgResult` and its return value will be used
     * as the new `cgResult`.
     * @returns A fresh `ApiResult` with `cgResult` set to the return value of `mapper`.
     */
    map(mapper) {
        return new ApiResult(this.status, this.body, this.headers, mapper(this.cgResult), [
            {
                onAfterSuccess: () => this.onAfterSuccess(),
                onSuccess: () => this.onSuccess(),
            },
        ], this.maybeExtraWarnings);
    }
    /**
     * Map the `cgResult` of this result with a function that can error.
     *
     * @param mapper The mapper, it is called with `cgResult` and should return an `Either`.
     * @returns If `mapper` returned a `Left` the result of `mapper`, otherwise a fresh `ApiResult`
     * with `cgResult` set to the extracted return of `mapper`.
     */
    mapEither(mapper) {
        return mapper(this.cgResult).map(cgResult => this.map(always(cgResult)));
    }
    /**
     * Add callbacks to an `ApiResult`.
     *
     * Note that these callbacks are not called by the result itself, but by other
     * classes/components in our tree. This is purely by convention and no errors will be raised
     * when callbacks are not called.
     *
     * @param methods The callbacks to add.
     * @returns A new `ApiResult` with the given callbacks added.
     */
    addCallbacks(...methods) {
        return new ApiResult(this.status, this.body, this.headers, this.cgResult, [...this.callbacks, ...methods], this.maybeExtraWarnings);
    }
    /**
     * Discard the body of the `APIResult`.
     *
     * @returns A new `ApiResult` of which the body is empty. The `cgResult` is still preserved and
     * the registered callbacks will still work.
     */
    withoutBody() {
        return new ApiResult(this.status, undefined, this.headers, this.cgResult, [
            {
                onAfterSuccess: () => this.onAfterSuccess(),
                onSuccess: () => this.onSuccess(),
            },
        ], this.maybeExtraWarnings);
    }
}
